import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Plus, Shield, CheckCircle, XCircle, PencilSimple, Trash, X, WarningCircle,
  ChartLine, Users, ListBullets, Gear, Robot, Eye, UsersThree, Copy, GoogleLogo, Lock, Link as LinkIcon,
  FlowArrow, Key,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  UsageOverviewTab, UsersUsageTab, UsageLogTab, PilotSettingsTab, AgentFlagsTab, WorkflowCoverageTab,
} from "./AdminUsageTabs";

/* ═══════════════════════════════════════════════════════════════════
 * Admin Dashboard — pilot user (allowlist) management.
 * Csak admin role-lal nyitható meg. Nem-admin esetén /dashboard-ra
 * redirect. NEM tartalmaz user-tartalmat (promptok, dokumentumok stb.).
 * ═══════════════════════════════════════════════════════════════════ */
export default function AdminDashboard() {
  const { user, loading, setPreviewAsTester, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview"); // overview | testers | logs | settings | agents | access
  const [usageMode, setUsageMode] = useState(() => {
    if (typeof window === "undefined") return "calibrated";
    const saved = window.localStorage.getItem("admin-usage-mode");
    return saved === "estimated" ? "estimated" : "calibrated";
  });
  const [users, setUsers] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const startTesterPreview = () => {
    setPreviewAsTester(true);
    toast.info("Tester nézet előnézet bekapcsolva.");
    navigate("/dashboard");
  };

  const fetchUsers = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data.users || []);
    } catch (err) {
      toast.error("Nem sikerült betölteni a pilot user listát.");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") fetchUsers();
  }, [user, fetchUsers]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("admin-usage-mode", usageMode);
  }, [usageMode]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-[#a1a1a1] text-sm">
        Betöltés…
      </div>
    );
  }
  // Preview módban (admin → effectiveRole='tester') is redirect — a tester
  // soha nem fér hozzá a /admin pulthoz.
  if (!user || effectiveRole !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen text-white" data-testid="admin-dashboard">
      <div className="max-w-[1200px] mx-auto px-8 pt-10 pb-16">
        {/* Header */}
        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2.5 text-[11px] tracking-[0.18em] uppercase text-[#a1a1a1] mb-3">
              <Shield size={14} weight="fill" color="#5e50f7" />
              Admin pult
            </div>
            <h1 className="font-heading text-[34px] tracking-tight font-semibold mb-2">
              Pilot kezelés
            </h1>
            <p className="text-[#a1a1a1] text-[14px] max-w-[640px] leading-relaxed">
              Hozzáférések, használati limitek, becsült AI-költségek és ügynök-elérhetőség.
              Az admin pult nem fér hozzá a tesztelők saját tartalmaihoz.
            </p>
          </div>
          {tab === "access" && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="admin-bulk-add-btn"
                onClick={() => setBulkOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-xl font-semibold transition hover:-translate-y-0.5"
                style={{
                  background: "rgba(94,80,247,0.12)",
                  border: "1px solid rgba(94,80,247,0.35)",
                  color: "#c8c1ff",
                  fontSize: "13.5px",
                }}
              >
                <UsersThree size={16} weight="bold" /> Tömeges hozzáadás
              </button>
              <button
                type="button"
                data-testid="admin-add-user-btn"
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-white transition hover:-translate-y-0.5"
                style={{
                  background: "linear-gradient(135deg, #5e50f7 0%, #4F46E5 100%)",
                  boxShadow: "0 12px 32px rgba(94,80,247,0.36)",
                  fontSize: "14px",
                }}
              >
                <Plus size={16} weight="bold" /> Új tesztelő
              </button>
            </div>
          )}
          {tab !== "access" && (
            <button
              type="button"
              data-testid="admin-tester-preview-btn"
              onClick={startTesterPreview}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition hover:-translate-y-0.5"
              style={{
                background: "rgba(94,80,247,0.10)",
                border: "1px solid rgba(94,80,247,0.35)",
                color: "#c8c1ff",
                fontSize: "13px",
              }}
              title="Tekintsd meg, mit lát egy sima tesztelő — kijelentkezés nélkül."
            >
              <Eye size={15} weight="fill" /> Tester nézet
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-white/[0.06]" data-testid="admin-tabs">
          {[
            ["overview", "Áttekintés", ChartLine],
            ["testers", "Tesztelők", Users],
            ["logs", "Usage log", ListBullets],
            ["coverage", "Workflow lefedettség", FlowArrow],
            ["settings", "Beállítások", Gear],
            ["agents", "Ügynökök", Robot],
            ["access", "Hozzáférések", Shield],
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`px-4 py-3 text-[13px] font-medium transition border-b-2 inline-flex items-center gap-2 ${
                tab === key ? "border-[#5e50f7] text-white" : "border-transparent text-[#a1a1a1] hover:text-white"
              }`}
            >
              <Icon size={14} weight={tab === key ? "fill" : "regular"} />
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && <UsageOverviewTab mode={usageMode} onModeChange={setUsageMode} />}
        {tab === "testers" && <UsersUsageTab mode={usageMode} />}
        {tab === "logs" && <UsageLogTab mode={usageMode} />}
        {tab === "coverage" && <WorkflowCoverageTab mode={usageMode} />}
        {tab === "settings" && <PilotSettingsTab />}
        {tab === "agents" && <AgentFlagsTab />}
        {tab === "access" && (
          <AccessTab
            user={user}
            users={users}
            listLoading={listLoading}
            fetchUsers={fetchUsers}
            setEditing={setEditing}
          />
        )}
      </div>

      {addOpen && (
        <UserFormModal
          mode="add"
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            fetchUsers();
          }}
        />
      )}
      {bulkOpen && (
        <BulkAddModal
          onClose={() => setBulkOpen(false)}
          onSaved={() => {
            fetchUsers();
          }}
        />
      )}
      {editing && (
        <UserFormModal
          mode="edit"
          initial={editing}
          isSelf={editing.email === user.email}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
}

/* ─── access-list tab (the original allowlist table) ─── */
function AccessTab({ user, users, listLoading, fetchUsers, setEditing }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-4 mb-8" data-testid="admin-stats">
        <StatCard label="Összes hozzáférés" value={users.length} />
        <StatCard
          label="Aktív"
          value={users.filter((u) => u.status === "active").length}
          accent="#22C55E"
        />
        <StatCard
          label="Admin"
          value={users.filter((u) => u.role === "admin").length}
          accent="#5e50f7"
        />
      </div>

      <div
        className="rounded-2xl overflow-hidden border border-white/[0.06]"
        style={{ background: "rgba(20,20,26,0.6)" }}
        data-testid="admin-user-table"
      >
        <div className="grid grid-cols-[1fr_1.4fr_100px_110px_140px_140px_140px] gap-3 px-6 py-4 text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] border-b border-white/[0.06] bg-[rgba(20,20,26,0.85)]">
          <div>Név</div><div>Email</div><div>Role</div><div>Státusz</div><div>Belépés</div><div>Utolsó</div><div className="text-right">Műveletek</div>
        </div>
        {listLoading ? (
          <div className="px-6 py-12 text-center text-[#a1a1a1] text-sm">Betöltés…</div>
        ) : users.length === 0 ? (
          <div className="px-6 py-12 text-center text-[#a1a1a1] text-sm">
            Még nincs hozzáférés. Adj hozzá egy új tesztelőt.
          </div>
        ) : (
          users.map((u) => (
            <UserRow
              key={u.email}
              u={u}
              isSelf={u.email === user.email}
              onEdit={() => setEditing(u)}
              onCopyInvite={async (email) => {
                try {
                  const res = await api.get(`/admin/users/${encodeURIComponent(email)}/invite-link`);
                  const link = res.data.invite_link;
                  await navigator.clipboard.writeText(link);
                  toast.success("Meghívó link a vágólapon.");
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Nem sikerült a link generálása.");
                }
              }}
              onCopyResetLink={async (email) => {
                try {
                  const res = await api.get(`/admin/users/${encodeURIComponent(email)}/reset-link`);
                  const link = res.data.reset_link;
                  await navigator.clipboard.writeText(link);
                  toast.success(`Jelszó-visszaállító link a vágólapon (${res.data.valid_minutes} percig érvényes).`);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Nem sikerült a reset link generálása.");
                }
              }}
              onChangeStatus={async (next) => {
                if (next === "inactive") {
                  const ok = window.confirm(
                    `Biztosan letiltod a hozzáférést: ${u.email}?\n\n` +
                    `A user a következő interakciónál automatikusan ki lesz léptetve, és` +
                    ` nem fog tudni új generálást indítani.`
                  );
                  if (!ok) return;
                }
                try {
                  await api.patch(`/admin/users/${encodeURIComponent(u.email)}`, { status: next });
                  toast.success(next === "active" ? "Aktiválva." : "Letiltva.");
                  fetchUsers();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Művelet sikertelen.");
                }
              }}
              onDelete={async () => {
                if (!window.confirm(`Biztosan törlöd: ${u.email}?`)) return;
                try {
                  await api.delete(`/admin/users/${encodeURIComponent(u.email)}`);
                  toast.success("Törölve.");
                  fetchUsers();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Törlés sikertelen.");
                }
              }}
            />
          ))
        )}
      </div>
    </>
  );
}

/* ─── apró segédkomponensek ─── */

function StatCard({ label, value, accent = "#a1a1a1" }) {
  return (
    <div
      className="rounded-2xl px-5 py-4 border border-white/[0.06]"
      style={{ background: "rgba(20,20,26,0.6)" }}
    >
      <div className="text-[11px] tracking-[0.14em] uppercase text-[#7c7c8a] mb-1.5">
        {label}
      </div>
      <div className="text-[28px] font-semibold" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function RolePill({ role }) {
  const isAdmin = role === "admin";
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
      style={{
        background: isAdmin ? "rgba(94,80,247,0.16)" : "rgba(255,255,255,0.06)",
        color: isAdmin ? "#a294ff" : "#c8c8d2",
      }}
    >
      {isAdmin ? <Shield size={11} weight="fill" /> : null}
      {isAdmin ? "Admin" : "Tester"}
    </span>
  );
}

const STATUS_INFO = {
  active:    { label: "Aktív",         desc: "A user aktiválta a fiókját, be tud lépni és használhatja a MEX Magicet a beállított limitek szerint." },
  invited:   { label: "Meghívva",      desc: "Az e-mail cím jóvá van hagyva, de a user még nem aktiválta a fiókját. Beléphet Google-fiókkal vagy regisztrálhat email + jelszóval. Első sikeres belépés után Aktív lesz." },
  inactive:  { label: "Inaktív",       desc: "Admin által letiltva. Nem tud belépni, akkor sem, ha korábban már aktív volt." },
  suspended: { label: "Felfüggesztve", desc: "A user hozzáférése admin által le van tiltva. Nem tud belépni, akkor sem, ha korábban már aktív volt." },
  expired:   { label: "Lejárt",        desc: "A hozzáférési idő lejárt. Nem tud belépni vagy használni az appot, amíg admin nem hosszabbítja meg vagy új hozzáférést nem kap." },
};

function StatusPill({ status }) {
  const style = {
    active:    { bg: "rgba(34,197,94,0.14)",   fg: "#4ade80", icon: <CheckCircle size={11} weight="fill" /> },
    invited:   { bg: "rgba(94,80,247,0.16)",   fg: "#a294ff", icon: <UsersThree size={11} weight="fill" /> },
    inactive:  { bg: "rgba(251,113,133,0.14)", fg: "#fb7185", icon: <XCircle size={11} weight="fill" /> },
    suspended: { bg: "rgba(251,146,60,0.14)",  fg: "#fb923c", icon: <WarningCircle size={11} weight="fill" /> },
    expired:   { bg: "rgba(148,163,184,0.16)", fg: "#94a3b8", icon: <XCircle size={11} weight="fill" /> },
  };
  const info = STATUS_INFO[status] || STATUS_INFO.inactive;
  const s = style[status] || style.inactive;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold cursor-help"
      style={{ background: s.bg, color: s.fg }}
      title={info.desc}
      data-testid={`admin-status-pill-${status}`}
    >
      {s.icon}
      {info.label}
    </span>
  );
}

function AuthMethodsBadge({ methods }) {
  const arr = Array.isArray(methods) ? methods : [];
  const hasG = arr.includes("google");
  const hasP = arr.includes("email_password");
  if (!hasG && !hasP) {
    return <span className="text-[10.5px] text-[#7c7c8a] italic">nincs</span>;
  }
  return (
    <div className="flex items-center gap-1.5">
      {hasG && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-medium" style={{ background: "rgba(255,255,255,0.06)", color: "#c8c8d2" }}>
          <GoogleLogo size={10} weight="bold" /> Google
        </span>
      )}
      {hasP && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-medium" style={{ background: "rgba(94,80,247,0.14)", color: "#a294ff" }}>
          <Lock size={10} weight="fill" /> Jelszó
        </span>
      )}
    </div>
  );
}

function UserRow({ u, isSelf, onEdit, onChangeStatus, onDelete, onCopyInvite, onCopyResetLink }) {
  const active = u.status === "active";
  const last = u.last_login_at ? new Date(u.last_login_at) : null;
  return (
    <div
      data-testid={`admin-user-row-${u.email}`}
      className="grid grid-cols-[1fr_1.4fr_100px_110px_140px_140px_140px] gap-3 px-6 py-4 items-center text-[13px] border-b border-white/[0.04] hover:bg-white/[0.02] transition"
    >
      <div className="truncate">
        {u.name || <span className="text-[#7c7c8a] italic">—</span>}
        {isSelf && (
          <span className="ml-2 text-[10px] tracking-[0.12em] uppercase text-[#5e50f7]">Te</span>
        )}
      </div>
      <div className="truncate text-[#c8c8d2]" title={u.email}>
        {u.email}
      </div>
      <div>
        <RolePill role={u.role} />
      </div>
      <div>
        <StatusPill status={u.status} />
      </div>
      <div>
        <AuthMethodsBadge methods={u.auth_methods} />
      </div>
      <div className="text-[#a1a1a1] text-[12px]">
        {last
          ? last.toLocaleString("hu-HU", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "—"}
      </div>
      <div className="flex items-center justify-end gap-1.5">
        <IconBtn
          title={active ? "Meghívó link újraküldése (support célra — a user adatai megmaradnak)" : "Meghívó link másolása"}
          onClick={() => onCopyInvite?.(u.email)}
          testid={`admin-copy-invite-${u.email}`}
        >
          <LinkIcon size={15} />
        </IconBtn>
        <IconBtn
          title="Jelszó-visszaállító link másolása (aktív usernél is; a user anyagai érintetlenek)"
          onClick={() => onCopyResetLink?.(u.email)}
          testid={`admin-copy-reset-${u.email}`}
        >
          <Key size={15} />
        </IconBtn>
        <IconBtn
          title={active ? "Letiltás" : "Aktiválás"}
          onClick={() => onChangeStatus(active ? "inactive" : "active")}
          disabled={isSelf && active}
          testid={`admin-toggle-${u.email}`}
        >
          {active ? <XCircle size={15} weight="fill" /> : <CheckCircle size={15} weight="fill" />}
        </IconBtn>
        <IconBtn title="Szerkesztés" onClick={onEdit} testid={`admin-edit-${u.email}`}>
          <PencilSimple size={15} />
        </IconBtn>
        <IconBtn
          title="Törlés"
          onClick={onDelete}
          danger
          disabled={isSelf}
          testid={`admin-delete-${u.email}`}
        >
          <Trash size={15} />
        </IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, danger = false, disabled = false, testid }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${
        disabled
          ? "opacity-30 cursor-not-allowed"
          : danger
          ? "text-[#fb7185] hover:bg-[rgba(251,113,133,0.10)]"
          : "text-[#a1a1a1] hover:text-white hover:bg-white/[0.06]"
      }`}
    >
      {children}
    </button>
  );
}

/* ─── Új / szerkesztés modal ─── */

function UserFormModal({ mode, initial, isSelf, onClose, onSaved }) {
  const editing = mode === "edit";
  const [email, setEmail] = useState(initial?.email || "");
  const [name, setName] = useState(initial?.name || "");
  const [role, setRole] = useState(initial?.role || "tester");
  const [status, setStatus] = useState(initial?.status || "invited");
  const [creditPlan, setCreditPlan] = useState(initial?.credit_plan || "MEX");
  const [initialCredit, setInitialCredit] = useState(""); // Sprint 3
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/users/${encodeURIComponent(initial.email)}`, {
          name,
          role,
          status,
          credit_plan: role === "admin" ? null : creditPlan,
        });
        toast.success("Mentve.");
      } else {
        const payload = { email, name, role, status, credit_plan: role === "admin" ? null : creditPlan };
        if (initialCredit !== "" && !isNaN(parseFloat(initialCredit))) {
          payload.initial_credit = parseFloat(initialCredit);
        }
        await api.post("/admin/users", payload);
        toast.success(`Új tesztelő hozzáadva${payload.initial_credit != null ? ` (${payload.initial_credit} MEX kredit)` : ""}.`);
      }
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: "rgba(8,8,12,0.78)", backdropFilter: "blur(8px)" }}
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="admin-user-form-modal"
    >
      <div
        className="relative w-full max-w-[440px] rounded-2xl text-white p-7"
        style={{
          background: "#16161f",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center text-[#a1a1a1] hover:text-white hover:bg-white/[0.06]"
        >
          <X size={14} />
        </button>
        <h3 className="font-heading text-[20px] font-semibold mb-1">
          {editing ? "Tesztelő szerkesztése" : "Új tesztelő hozzáadása"}
        </h3>
        <p className="text-[12.5px] text-[#a1a1a1] mb-6">
          {editing
            ? "A módosítás azonnal hatályba lép. Ha a user éppen be van lépve, a változás a következő kérésre érvényesül."
            : "Add hozzá az engedélyezett e-mail címet. A user a Google fiókjával fog tudni belépni."}
        </p>

        <div className="space-y-4">
          <Field label="Név">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="admin-form-name"
              placeholder="pl. Kovács Anna"
              className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]"
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={editing}
              data-testid="admin-form-email"
              placeholder="pl. anna@ceg.hu"
              className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7] disabled:opacity-50"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Role">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={isSelf && role === "admin"}
                data-testid="admin-form-role"
                className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7] disabled:opacity-50"
              >
                <option value="tester">Tester</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Field label="Státusz">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={isSelf}
                data-testid="admin-form-status"
                className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7] disabled:opacity-50"
              >
                <option value="invited">Meghívva</option>
                <option value="active">Aktív</option>
                <option value="inactive">Inaktív</option>
                <option value="suspended">Felfüggesztve</option>
                <option value="expired">Lejárt</option>
              </select>
            </Field>
          </div>
          <Field label="MXC csomag">
            <select
              value={role === "admin" ? "" : creditPlan}
              onChange={(e) => setCreditPlan(e.target.value)}
              disabled={role === "admin"}
              data-testid="admin-form-credit-plan"
              className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7] disabled:opacity-50"
            >
              <option value="">Admin / nincs csomag</option>
              <option value="MEX">MEX</option>
              <option value="MEX+">MEX+</option>
              <option value="SPRINT">SPRINT</option>
              <option value="EGYEDI">EGYEDI</option>
            </select>
          </Field>
          {!editing && (
            <Field label="Induló MEX kredit (opcionális — ha üres, credit_config default érvényes)">
              <input
                type="number"
                step="1"
                min="0"
                value={initialCredit}
                onChange={(e) => setInitialCredit(e.target.value)}
                data-testid="admin-form-initial-credit"
                placeholder="pl. 100"
                className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]"
              />
            </Field>
          )}
        </div>

        {error && (
          <div className="mt-4 text-[12.5px] text-[#fb7185] flex items-start gap-2">
            <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-7">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg text-[13px] text-[#a1a1a1] hover:text-white hover:bg-white/[0.06] transition"
          >
            Mégse
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || (!editing && !email)}
            data-testid="admin-form-save"
            className="px-5 py-2.5 rounded-lg font-semibold text-white text-[13px] transition disabled:opacity-50"
            style={{
              background: "linear-gradient(135deg, #5e50f7 0%, #4F46E5 100%)",
              boxShadow: "0 8px 24px rgba(94,80,247,0.40)",
            }}
          >
            {saving ? "Mentés…" : editing ? "Mentés" : "Hozzáadás"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

/* ─── Tömeges hozzáadás modal ─── */

function BulkAddModal({ onClose, onSaved }) {
  const [emails, setEmails] = useState("");
  const [role, setRole] = useState("tester");
  const [creditPlan, setCreditPlan] = useState("MEX");
  const [initialCredit, setInitialCredit] = useState(""); // Sprint 3
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = { emails, role, status: "invited", credit_plan: role === "admin" ? null : creditPlan };
      if (initialCredit !== "" && !isNaN(parseFloat(initialCredit))) {
        payload.initial_credit = parseFloat(initialCredit);
      }
      const res = await api.post("/admin/users/bulk", payload);
      setResult(res.data);
      const addedCount = (res.data.added || []).length;
      if (addedCount > 0) {
        toast.success(`${addedCount} új meghívott felvéve${payload.initial_credit != null ? ` (${payload.initial_credit} MEX kredit)` : ""}.`);
        onSaved?.();
      } else if ((res.data.invalid || []).length || (res.data.duplicates || []).length) {
        toast.info("A lista áttekinthető — nézd meg az eredményt.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Tömeges hozzáadás sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const copyAllLinks = async () => {
    if (!result || !result.links) return;
    const lines = Object.entries(result.links)
      .map(([email, link]) => `${email}: ${link}`)
      .join("\n");
    await navigator.clipboard.writeText(lines);
    toast.success("Az összes meghívó link a vágólapon.");
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 overflow-y-auto"
      style={{ background: "rgba(8,8,12,0.78)", backdropFilter: "blur(8px)" }}
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="admin-bulk-add-modal"
    >
      <div
        className="relative w-full max-w-[600px] rounded-2xl text-white p-7 my-auto"
        style={{ background: "#16161f", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 30px 80px rgba(0,0,0,0.6)" }}
      >
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center text-[#a1a1a1] hover:text-white hover:bg-white/[0.06]">
          <X size={14} />
        </button>
        <h3 className="font-heading text-[20px] font-semibold mb-1">Tömeges hozzáadás</h3>
        <p className="text-[12.5px] text-[#a1a1a1] mb-6 leading-relaxed">
          Másold be az e-mail címeket. Elválasztható sortöréssel, vesszővel vagy pontosvesszővel.
          Mindenki <b>meghívott</b> státusszal kerül fel — első Google-belépéskor vagy regisztrációkor lesz aktív.
        </p>

        <div className="space-y-4">
          <Field label="E-mail címek">
            <textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              data-testid="admin-bulk-emails"
              placeholder={"pl.\nkovacs.anna@ceg.hu\nnagy.bela@gmail.com\ntoth.piri@yahoo.com"}
              rows={9}
              className="w-full px-4 py-3 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[13.5px] focus:outline-none focus:border-[#5e50f7] font-mono resize-y"
            />
          </Field>
          <Field label="Alapértelmezett role">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="admin-bulk-role"
              className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]"
            >
              <option value="tester">Tester</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="MXC csomag">
            <select
              value={role === "admin" ? "" : creditPlan}
              onChange={(e) => setCreditPlan(e.target.value)}
              disabled={role === "admin"}
              data-testid="admin-bulk-credit-plan"
              className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7] disabled:opacity-50"
            >
              <option value="">Admin / nincs csomag</option>
              <option value="MEX">MEX</option>
              <option value="MEX+">MEX+</option>
              <option value="SPRINT">SPRINT</option>
              <option value="EGYEDI">EGYEDI</option>
            </select>
          </Field>
          <Field label="Induló MEX kredit (opcionális — ha üres, credit_config default)">
            <input
              type="number"
              step="1"
              min="0"
              value={initialCredit}
              onChange={(e) => setInitialCredit(e.target.value)}
              data-testid="admin-bulk-initial-credit"
              placeholder="pl. 100"
              className="w-full px-4 py-2.5 rounded-lg bg-[#0f0f17] border border-white/[0.08] text-[14px] focus:outline-none focus:border-[#5e50f7]"
            />
          </Field>
        </div>

        {error && (
          <div className="mt-4 text-[12.5px] text-[#fb7185] flex items-start gap-2">
            <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="mt-5 space-y-3" data-testid="admin-bulk-result">
            <div className="grid grid-cols-3 gap-2 text-[12px]">
              <ResultCard label="Hozzáadva" value={(result.added || []).length} color="#4ade80" />
              <ResultCard label="Duplikáció" value={(result.duplicates || []).length} color="#fbbf24" />
              <ResultCard label="Érvénytelen" value={(result.invalid || []).length} color="#fb7185" />
            </div>
            {result.duplicates?.length > 0 && (
              <ResultList title="Már létező (kihagyva)" items={result.duplicates} />
            )}
            {result.invalid?.length > 0 && (
              <ResultList title="Érvénytelen formátum (kihagyva)" items={result.invalid} />
            )}
            {result.links && Object.keys(result.links).length > 0 && (
              <div className="rounded-lg border border-white/[0.06] bg-[#0f0f17] p-3.5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11.5px] tracking-[0.12em] uppercase text-[#7c7c8a] font-semibold">
                    Meghívó linkek (kézi továbbküldéshez)
                  </div>
                  <button
                    type="button"
                    onClick={copyAllLinks}
                    data-testid="admin-bulk-copy-all-links"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-semibold text-[#5e50f7] hover:bg-[#5e50f7]/10 transition"
                  >
                    <Copy size={12} weight="bold" /> Összes másolása
                  </button>
                </div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto text-[11.5px]">
                  {Object.entries(result.links).map(([email, link]) => (
                    <div key={email} className="flex items-center gap-2 group">
                      <button
                        type="button"
                        onClick={async () => {
                          await navigator.clipboard.writeText(link);
                          toast.success(`Link másolva: ${email}`);
                        }}
                        className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-[#a1a1a1] hover:text-white hover:bg-white/[0.08] transition"
                        title="Link másolása"
                      >
                        <Copy size={11} weight="bold" />
                      </button>
                      <span className="text-[#c8c8d2] truncate">{email}</span>
                      <span className="text-[#5e50f7] font-mono truncate flex-1" title={link}>{link}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg text-[13px] text-[#a1a1a1] hover:text-white hover:bg-white/[0.06] transition"
          >
            {result ? "Bezárás" : "Mégse"}
          </button>
          {!result && (
            <button
              type="button"
              onClick={submit}
              disabled={saving || !emails.trim()}
              data-testid="admin-bulk-submit"
              className="px-5 py-2.5 rounded-lg font-semibold text-white text-[13px] transition disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #5e50f7 0%, #4F46E5 100%)", boxShadow: "0 8px 24px rgba(94,80,247,0.40)" }}
            >
              {saving ? "Feldolgozás…" : "Hozzáadás"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultCard({ label, value, color }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#0f0f17] px-3.5 py-2.5">
      <div className="text-[10.5px] tracking-[0.12em] uppercase text-[#7c7c8a]">{label}</div>
      <div className="text-[18px] font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}

function ResultList({ title, items }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#0f0f17] p-3">
      <div className="text-[11.5px] tracking-[0.12em] uppercase text-[#7c7c8a] mb-1.5 font-semibold">{title}</div>
      <div className="text-[12px] text-[#c8c8d2] space-y-0.5 max-h-24 overflow-y-auto font-mono">
        {items.map((e) => <div key={e}>{e}</div>)}
      </div>
    </div>
  );
}
